import get from 'lodash/get';
import entries from 'lodash/entries';

/**
 * Checks if a field from Sitecore has a value. Used for conditional
 * rendering, avoiding breaks.
 * @param {*} field Sitecore field.
 */
export function fieldHasValue(field = {}) {
  return Boolean(get(field, 'value'));
}

/**
 * Used to fill empty fields coming from Sitecore to avoid js errors.
 * If a field doesn't exist in the defaultValues object, it will use the original.
 * If the field doesn't exist in the original object, it won't merge the default values.
 *
 * Example:
 *
 * fillEmptyValues(fields, { a: '', b: 'Bar' })
 *
 * If fields is compound by a = 'Foo' and b being undefined, this is the result.
 *
 * {
 *  a: { value: 'Foo' },
 *  b: { value: 'Bar' },
 * }
 *
 * @param {object} fields Sitecore fields.
 * @param {object} [defaultValues] Object with default values for the fields that need it.
 */
export function fillEmptyValues(fields = {}, defaultValues = {}) {
  return Object.entries(fields).reduce((acc, [key, field]) => {
    if (!fieldHasValue(field) && defaultValues[key]) {
      acc[key] = {
        value: defaultValues[key],
      };
    } else {
      acc[key] = field;
    }

    return acc;
  }, {});
}

/**
 * Creates an object with a `fields` property that matches the basic
 * sitecore structure. e.g.
 *
 * // Input
 * createFields({ a: 'b', c: 'd' })
 *
 * // Output
 * {
 *   fields: {
 *     a: { value: 'b' },
 *     c: { value: 'd' },
 *   }
 * }
 *
 * For more examples, check the tests.
 */
export function createFields(options) {
  if (!options) {
    return null;
  }

  return {
    fields: entries(options).reduce(
      (finalFields, [key, value]) => ({
        ...finalFields,
        [key]: { value },
      }),
      {},
    ),
  };
}
